/**
 * Env-driven logging config for this app — backend selection
 * (`VITE_LOG_BACKEND`), core fields (service/environment/version), and the
 * debug gate (`VITE_LOG_DEBUG`).
 *
 * This is the one place in the app that reads those variables for logging
 * purposes. @bindery/logging itself stays environment-agnostic (see
 * packages/logging/src/core-fields.ts), and the template has no env-parsing
 * framework to route them through — `import.meta.env` is read directly here,
 * with `parseLogBackend` doing the one piece of validation that matters.
 *
 * ./earlyErrorCapture.ts deliberately does NOT import this module: it runs
 * before the rest of the app's module graph and reads the same variables for
 * itself. What keeps the two from disagreeing about a malformed value is that
 * both classify it with the same `parseLogBackend`.
 */
import {
  ConsoleLogBackend,
  type ContextProvider,
  type CoreFieldsInput,
  type LogBackend,
  type LogBackendChoice,
  Logger,
  MultiLogBackend,
  parseLogBackend,
  WorkerLogBackend,
} from "@bindery/logging";
import { LOG_SERVICE_NAME } from "../site";

/** Whether the ignored-value warning below has already been printed. */
let warnedAboutBackend = false;

/**
 * The configured backend, plus a one-time warning when the configured value
 * was not one this build understands.
 *
 * `parseLogBackend` decides what an unrecognized value *means* — console-only,
 * so a misconfiguration never starts POSTing to a network sink. Noticing it is
 * this module's job rather than the package's: `"axiom "` with a trailing
 * space (the shell/CI typo) otherwise boots a build that silently sends
 * nothing, and an operator who set the variable deserves to be told the value
 * was ignored. Warned once rather than per `createLogger()` call, because this
 * runs again for every hook call and every non-hook factory call.
 */
function resolveBackendChoice(): LogBackendChoice {
  const configured: unknown = import.meta.env.VITE_LOG_BACKEND;
  const choice = parseLogBackend(configured);

  const wasIgnored =
    configured !== undefined && configured !== "" && configured !== choice;
  if (wasIgnored && !warnedAboutBackend) {
    warnedAboutBackend = true;
    console.warn(
      `[logging] ignoring VITE_LOG_BACKEND=${JSON.stringify(configured)} — logging through "${choice}" instead`,
    );
  }

  return choice;
}

/**
 * Core fields for every Logger this app constructs.
 *
 * `environment` comes from Vite's own `import.meta.env.MODE` rather than a
 * `VITE_*` var — it is Vite's build-mode signal ("development"/"production"),
 * not app config. `version` is optional and unset in a fresh clone; wiring a
 * git SHA into `VITE_APP_VERSION` at build time is the intended way to fill
 * it. An empty string is treated as absent, because that is what an env var
 * declared but never assigned arrives as, and the ingest schema wants either a
 * real version or no field at all.
 */
export function createCoreFieldsInput(): CoreFieldsInput {
  const version: unknown = import.meta.env.VITE_APP_VERSION;
  return {
    service: LOG_SERVICE_NAME,
    environment: import.meta.env.MODE,
    version:
      typeof version === "string" && version !== "" ? version : undefined,
  };
}

/**
 * Gate for `Logger.debug()`. Off unless `VITE_LOG_DEBUG` is exactly `"true"`
 * — env vars are strings, and treating every non-empty value as truthy would
 * make `VITE_LOG_DEBUG=false` turn debug logging on.
 */
export function isDebugEnabled(): boolean {
  return import.meta.env.VITE_LOG_DEBUG === "true";
}

/**
 * Build the backend for the configured `VITE_LOG_BACKEND` value.
 *
 * - `"console"` — ConsoleLogBackend only. The default, and what a fresh clone
 *   runs with: structured wide events in the devtools console, no
 *   infrastructure to configure.
 * - `"axiom"` — WorkerLogBackend only, POSTing to `/api/log` (which is inert
 *   until a project activates apps/retells/worker/api-log.ts.example).
 * - `"both"` — WorkerLogBackend + ConsoleLogBackend fanned out via
 *   MultiLogBackend.
 *
 * Anything else, including an unset value, has already become `"console"` in
 * `resolveBackendChoice` above, so this switch is exhaustive over the three
 * real cases and needs no `default:` arm.
 */
export function createBackend(): LogBackend {
  switch (resolveBackendChoice()) {
    case "console":
      return new ConsoleLogBackend();
    case "axiom":
      return new WorkerLogBackend();
    case "both":
      return new MultiLogBackend([
        new WorkerLogBackend(),
        new ConsoleLogBackend(),
      ]);
  }
}

/**
 * Build a `Logger` wired to this app's env-driven config, for the given
 * context provider. The one construction path shared by LoggerProvider (one
 * Logger for the whole tree), useLogger (one per hook call, memoized), and
 * createClientLogger (one per non-hook caller) — each supplies its own
 * `contextProvider`, everything else about the Logger is identical.
 */
export function createLogger(contextProvider: ContextProvider): Logger {
  return new Logger(
    createBackend(),
    contextProvider,
    createCoreFieldsInput(),
    isDebugEnabled(),
  );
}

/**
 * The one `ContextProvider` shape this app uses, so the three call sites that
 * build one cannot drift apart. Authenticated call sites layer in your auth
 * provider's opaque subject as `userId`; the provider-level logger has no user
 * identity.
 */
export function makeContextProvider(
  sessionId: string,
  userId?: string,
  leadId?: string,
): ContextProvider {
  return {
    getSessionId: () => sessionId,
    getUserId: () => userId,
    getLeadId: () => leadId,
  };
}
